/* assets/where-to-buy.js
 * Where to Buy store locator.
 *
 * Port of the inline script on the Webflow original. Behaviour kept as-is:
 *   - map centred/zoomed differently under and over the mobile breakpoint
 *   - store pins drawn as a symbol layer using a marker image, not DOM markers
 *   - clicking a list row flies to that store
 *   - clicking a pin opens a popup with the store name
 *   - after a zoom or pan, the list is filtered to the pins currently rendered
 *
 * Two deliberate differences from the original, both noted in
 * WHERE-TO-BUY-MIGRATION.md:
 *   1. The original calls reset_location_list([]) to hide every row and then
 *      re-shows the visible ones, which flashes the whole list on every pan.
 *      This diffs the visible set instead, so only rows that actually change
 *      state animate. Same rows end up visible.
 *   2. The original depends on jQuery for hide()/show(); the theme ships none,
 *      so visibility is a [hidden] attribute with a CSS transition.
 */
(function () {
  'use strict';

  var GL_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js';
  var GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css';
  var SEARCH_JS = 'https://api.mapbox.com/search-js/v1.5.0/web.js';
  var LAYER_ID = 'unclustered-point';
  var SOURCE_ID = 'locations';
  var MARKER_ID = 'default-marker';

  function loadStyle(href) {
    if (document.querySelector('link[href="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.addEventListener('load', function () {
        s.dataset.loaded = 'true';
        resolve();
      });
      s.addEventListener('error', function () {
        reject(new Error('Failed to load ' + src));
      });
      document.head.appendChild(s);
    });
  }

  function readOptions(root) {
    var d = root.dataset;
    var num = function (key, fallback) {
      var v = parseFloat(d[key]);
      return isNaN(v) ? fallback : v;
    };
    var flag = function (key) { return d[key] === 'true'; };
    return {
      token: d.token || '',
      style: d.style || 'mapbox://styles/mapbox/standard',
      projection: d.projection || 'albers',
      markerIcon: d.markerIcon || '',
      markerSize: num('markerSize', 1),
      maxZoom: num('maxZoom', 16),
      flyToZoom: num('flyToZoom', 14),
      mobileBreakpoint: num('mobileBreakpoint', 768),
      mobileCenter: [num('mobileLng', -111.8118168), num('mobileLat', 40.7062377)],
      desktopCenter: [num('desktopLng', -111.883921), num('desktopLat', 40.622859)],
      mobileZoom: num('mobileZoom', 9),
      desktopZoom: num('desktopZoom', 10),
      enableSearch: flag('enableSearch'),
      filterList: flag('filterList'),
      scrollZoom: flag('scrollZoom'),
      // Basemap import config. Mirrors the Webflow style's own settings.
      config: {
        theme: d.cfgTheme || 'monochrome',
        lightPreset: d.cfgLightPreset || 'night',
        font: d.cfgFont || 'Oswald',
        colorPlaceLabels: d.cfgColorPlaceLabels || '#b75c1d',
        show3dObjects: flag('cfg3d'),
        showPointOfInterestLabels: flag('cfgPoi'),
        showTransitLabels: flag('cfgTransit'),
        showRoadLabels: flag('cfgRoadLabels'),
        showPedestrianRoads: flag('cfgPedestrian')
      }
    };
  }

  /* Build the GeoJSON from the rendered rows, the way the original reads its
     hidden .location list. Rows without usable coordinates are skipped. */
  function collectFeatures(items) {
    var features = [];
    items.forEach(function (el) {
      var lat = parseFloat(el.getAttribute('data-lat'));
      var lng = parseFloat(el.getAttribute('data-lng'));
      if (isNaN(lat) || isNaN(lng)) return;
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: {
          slug: el.getAttribute('data-slug') || '',
          name: el.getAttribute('data-name') || '',
          address: el.getAttribute('data-address') || '',
          phone: el.getAttribute('data-phone') || ''
        }
      });
    });
    return { type: 'FeatureCollection', features: features };
  }

  function init(root) {
    var opts = readOptions(root);
    var mapEl = root.querySelector('[data-wtb-map]');
    var listEl = root.querySelector('[data-wtb-list]');
    var searchEl = root.querySelector('[data-wtb-search]');
    var emptyEl = root.querySelector('[data-wtb-empty]');
    if (!mapEl || !listEl) return;

    var items = Array.prototype.slice.call(
      listEl.querySelectorAll('[data-wtb-item]')
    );
    var data = collectFeatures(items);

    mapboxgl.accessToken = opts.token;
    var isMobile = window.innerWidth < opts.mobileBreakpoint;

    var map = new mapboxgl.Map({
      container: mapEl,
      style: opts.style,
      config: { basemap: opts.config },
      center: isMobile ? opts.mobileCenter : opts.desktopCenter,
      zoom: isMobile ? opts.mobileZoom : opts.desktopZoom,
      maxZoom: opts.maxZoom,
      projection: opts.projection
    });

    map.addControl(new mapboxgl.NavigationControl());
    if (!opts.scrollZoom) map.scrollZoom.disable();

    /* The age gate renders the body at zero width. A map built while that is up
       has a zero-size canvas and stays blank once it lifts, so watch for the
       container gaining size and resize. Not in the original, which has no gate. */
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () { map.resize(); });
      ro.observe(mapEl);
    }

    setUpList(map, items, opts);

    map.on('load', function () {
      if (opts.markerIcon) {
        map.loadImage(opts.markerIcon, function (err, image) {
          if (err || !image) return;
          if (!map.hasImage(MARKER_ID)) map.addImage(MARKER_ID, image);
          addPins(map, data, opts);
        });
      } else {
        addPins(map, data, opts);
      }
    });

    if (opts.enableSearch && searchEl) {
      attachSearch(map, searchEl, opts);
    }

    if (opts.filterList) {
      attachViewportFilter(map, items, emptyEl);
    }
  }

  var activePopup = null;

  function telHref(phone) {
    return String(phone).replace(/[^0-9+]/g, '');
  }

  function addPins(map, data, opts) {
    if (map.getSource(SOURCE_ID)) return;
    map.addSource(SOURCE_ID, {
      type: 'geojson',
      cluster: false,
      maxzoom: 24,
      data: data
    });
    map.addLayer({
      id: LAYER_ID,
      type: 'symbol',
      slot: 'top',
      source: SOURCE_ID,
      minzoom: 0,
      maxzoom: 24,
      layout: {
        'icon-image': map.hasImage(MARKER_ID) ? MARKER_ID : '',
        'icon-size': opts.markerSize,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-allow-overlap': true,
        'text-ignore-placement': true
      }
    });

    map.on('click', LAYER_ID, function (e) {
      if (!e.features || !e.features.length) return;
      var p = e.features[0].properties;
      var html = '<div class="bf-wtb-popup">'
        + '<div class="bf-wtb-popup__name">' + escapeHtml(p.name || '') + '</div>';
      if (p.address) {
        html += '<div class="bf-wtb-popup__address">' + escapeHtml(p.address) + '</div>';
      }
      if (p.phone) {
        html += '<a class="bf-wtb-popup__phone" href="tel:' + telHref(p.phone) + '">'
          + escapeHtml(p.phone) + '</a>';
      }
      html += '</div>';

      if (activePopup) activePopup.remove();
      activePopup = new mapboxgl.Popup({
        offset: 26,
        maxWidth: '260px',
        className: 'bf-wtb-popup-shell',
        focusAfterOpen: false
      })
        .setLngLat(e.features[0].geometry.coordinates.slice())
        .setHTML(html)
        .addTo(map);
    });
    map.on('mouseenter', LAYER_ID, function () {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', LAYER_ID, function () {
      map.getCanvas().style.cursor = '';
    });
  }

  function setUpList(map, items, opts) {
    items.forEach(function (el) {
      el.addEventListener('click', function () {
        var lat = parseFloat(el.getAttribute('data-lat'));
        var lng = parseFloat(el.getAttribute('data-lng'));
        if (isNaN(lat) || isNaN(lng)) return;
        map.flyTo({ center: [lng, lat], zoom: opts.flyToZoom });
      });
    });
  }

  /* After a zoom or pan, show only the rows whose pin is currently rendered.
     Matches the original's zoomend/moveend behaviour without its hide-all flash. */
  function attachViewportFilter(map, items, emptyEl) {
    var applying = false;

    function apply() {
      if (applying || !map.getLayer(LAYER_ID)) return;
      applying = true;
      var visible = new Set();
      map.queryRenderedFeatures({ target: { layerId: LAYER_ID } })
        .forEach(function (f) {
          if (f.properties && f.properties.slug) visible.add(f.properties.slug);
        });

      var shown = 0;
      items.forEach(function (el) {
        var on = visible.has(el.getAttribute('data-slug'));
        if (on) shown++;
        if (el.hasAttribute('hidden') === on) el.toggleAttribute('hidden', !on);
      });
      if (emptyEl) emptyEl.hidden = shown !== 0;
      applying = false;
    }

    map.on('zoomend', apply);
    map.on('moveend', apply);
  }

  function attachSearch(map, container, opts) {
    loadScript(SEARCH_JS).then(function () {
      if (!window.mapboxsearch || !window.mapboxsearch.MapboxSearchBox) return;
      var box = new window.mapboxsearch.MapboxSearchBox();
      box.accessToken = opts.token;
      box.options = { language: 'en' };
      box.mapboxgl = window.mapboxgl;
      box.marker = true;
      box.bindMap(map);
      container.appendChild(box);
    }).catch(function () { /* search is optional; map still works without it */ });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function boot() {
    var roots = Array.prototype.slice.call(
      document.querySelectorAll('[data-where-to-buy]')
    );
    if (!roots.length) return;

    var missing = roots.filter(function (r) { return !readOptions(r).token; });
    missing.forEach(function (r) {
      var n = r.querySelector('[data-wtb-no-token]');
      if (n) n.hidden = false;
    });
    roots = roots.filter(function (r) { return readOptions(r).token; });
    if (!roots.length) return;

    loadStyle(GL_CSS);
    loadScript(GL_JS).then(function () {
      roots.forEach(init);
    }).catch(function (e) {
      if (window.console) console.error('[where-to-buy]', e);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: rebuild when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-where-to-buy]')) boot();
  });
})();
